import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Text, useTheme } from '@Sage/hoi-poi-ui';

import Loading from 'components/Loading';
import { getLiteral } from 'utils/getLiteral';

import { MarketingTemplatesLibraryEmpty } from '../../templates/components/MarketingTemplatesEmptyStates';
import PredefinedTemplateCard from '../../templates/create/components/PredefinedTemplateCard';
import { useMarketingTemplates } from '../../templates/hooks/useMarketingTemplates';

import '../../templates/contents/MarketingTemplatesList.scss';
import '../../templates/create/styles.scss';
import './styles.scss';

const CampaignTemplateSelector = memo(({ isOpen, footerPreview, onBack, onSelectTemplate }) => {
    const theme = useTheme();
    const { templates, isLoading, error, refetch } = useMarketingTemplates();

    if (!isOpen) {
        return null;
    }

    const showError = Boolean(error) && templates.length === 0;
    const showEmpty = !isLoading && !showError && templates.length === 0;

    return (
        <div className="campaign-template-selector">
            <div className="campaign-template-selector__toolbar marketing-templates-create__toolbar">
                <button
                    type="button"
                    className="marketing-templates-create__back"
                    onClick={onBack}
                    aria-label={getLiteral('action_back')}
                >
                    <Icon name="arrowBack" color={theme.colors.actionMajor[500]} />
                </button>
                <Text type="h5" bold className="marketing-templates-create__title">
                    {getLiteral('label_marketing_campaigns_templates_select_title')}
                </Text>
            </div>

            <div className="campaign-template-selector__body marketing-templates-create__body">
                {isLoading && (
                    <div className="marketing-templates-create__loading">
                        <Loading />
                    </div>
                )}

                {showError && (
                    <div className="marketing-templates-list__error">
                        <Text type="body">{getLiteral('error_generalerror')}</Text>
                        <Button type="secondary" onClick={refetch}>
                            {getLiteral('label_retry')}
                        </Button>
                    </div>
                )}

                {showEmpty && <MarketingTemplatesLibraryEmpty />}

                {!isLoading && !showError && templates.length > 0 && (
                    <div className="marketing-templates-grid">
                        {templates.map((row, index) => (
                            <PredefinedTemplateCard
                                key={row.id != null ? String(row.id) : `tpl-${index}`}
                                footerPreview={footerPreview}
                                template={row}
                                onUseTemplate={onSelectTemplate}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
});

CampaignTemplateSelector.displayName = 'CampaignTemplateSelector';

CampaignTemplateSelector.propTypes = {
    isOpen: PropTypes.bool,
    footerPreview: PropTypes.shape({
        senderName: PropTypes.string,
        senderAddress: PropTypes.string,
        footerHere: PropTypes.string,
        linkHref: PropTypes.string,
    }),
    onBack: PropTypes.func,
    onSelectTemplate: PropTypes.func,
};

export default CampaignTemplateSelector;
